// src/components/EpisodeDetailModal.js
// Author: Sunil Jain 

import React, { useState, useMemo, Suspense, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, Grid, CircularProgress,
    Alert, Paper, Stack, Chip, Tooltip, TextField, Button, DialogActions,
    Tabs, Tab, alpha, Divider, List, ListItem, ListItemText,
    FormControl, InputLabel, Select, MenuItem, Menu, ListItemIcon
} from '@mui/material';
import { format, parseISO } from 'date-fns';

// Icons
import { jsPDF } from 'jspdf';
import {
    Close as CloseIcon, Delete as DeleteIcon, Visibility as VisibilityIcon,
    Assignment as AssignmentIcon, Dashboard as DashboardIcon, MoreVert as MoreVertIcon,
    PlaylistAdd as PlaylistAddIcon, Vaccines as VaccinesIcon, FactCheck as FactCheckIcon,
    Scoreboard as ScoreboardIcon, ContentCopy as ContentCopyIcon, Campaign as CampaignIcon,
    CheckCircleOutline as CheckCircleOutlineIcon, MonitorHeart as MonitorHeartIcon,
    DirectionsRun as ActivityIcon, Sick as SymptomsIcon,
    Fastfood as DietIcon, EditNote as JournalIcon, SentimentSatisfied as MoodIcon,
    ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import FolderOffIcon from '@mui/icons-material/FolderOff';

// Local Components
import authFetch from '../utils/authFetch';
import ReadOnlySurveyRenderer from './ReadOnlySurveyRenderer';
import Autocomplete from '@mui/material/Autocomplete';
import MedicationsDashboard from './MedicationsDashboard';
import KeyIndicatorsDashboard from './KeyIndicatorsDashboard';
import PatientStatusDashboard from './PatientStatusDashboard';
// [FIX] Import the InvestigationModal
import InvestigationModal from './InvestigationModal';

// API Functions
const fetchPatientDirectives = (patientId) => authFetch(`patients/${patientId}/directives`);
const ClinicianMiniCalendar = React.lazy(() => import('./ClinicianMiniCalendar'));
const VitalsDashboard = React.lazy(() => import('./VitalsDashboard'));
const MoodDashboard = React.lazy(() => import('./MoodDashboard'));
const SymptomsDashboard = React.lazy(() => import('./SymptomsDashboard'));
const ActivityDashboard = React.lazy(() => import('./ActivityDashboard'));
const SummariesDashboard = React.lazy(() => import('./SummariesDashboard'));
const JournalDashboard = React.lazy(() => import('./JournalDashboard'));
const LONGITUDINAL_CONTENT_TYPES = [
    'medication_adherence_check', 'vital_log_request', 'activity_log_request',
    'diet_log_request', 'symptom_log_request', 'feeling_check_in', 'journal_entry_request'
];

// --- Constants & Helpers ---
const ReminderFrequencyEnum = { ONCE: 'ONCE', WEEKLY: 'WEEKLY'};
const patientEpisodesQueryKey = (patientId) => ['patient', patientId, 'episodesV8'];
const contentItemsQueryKey = (tenantId) => ['allContentItemsForAssignment', tenantId];
const fetchAllContentItemsAPI = async ({ queryKey }) => { const [, tenantId] = queryKey; if (!tenantId) return []; const params = new URLSearchParams({ status: 'active', latest_only: 'true', limit: '500', tenant_id: tenantId }); const data = await authFetch(`/content/?${params.toString()}`); return Array.isArray(data) ? data : []; };
const formatDisplayValue = (value) => value ? String(value).replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) : 'N/A';
const getStatusChipProps = (status, variant = "outlined") => { const s = status ? String(status).toUpperCase() : 'UNKNOWN'; const props = { label: formatDisplayValue(s), size: "small", variant: "outlined" }; if (s.includes('ACTIVE')) return { ...props, color: "success" }; if (s.includes('PENDING')) return { ...props, color: "warning" }; if (s.includes('STOPPED') || s.includes('CANCELLED')) return { ...props, color: "error" }; if (s.includes('SUBMITTED')) return { ...props, color: "info" }; if (s.includes('COMPLETED')) return { ...props, color: "success", variant: 'filled' }; return { ...props, color: "default" }; };
const EPISODE_TYPE_MAP = { 'LIFETIME_PERPETUAL': { title: 'My Health Journal' }, 'ONBOARDING': { title: 'Pre-Encounter Care' }, 'TCM': { title: 'Transitional Care' }, 'RTM_MUSCULOSKELETAL': { title: 'RTM - Musculoskeletal' }, 'RTM_RESPIRATORY': { title: 'RTM - Respiratory' }, 'RTM': { title: 'Remote Therapeutic Monitoring' }, 'CCM': { title: 'Chronic Care Management' }, 'PCM': { title: 'Principal Care Management' }, 'BHI': { title: 'Behavioral Health Integration' }, 'DEFAULT': { title: 'Care Episode' }, };


// --- Sub-Components ---
const AssignmentListItem = ({ assignment, onDelete, onView, onSendReminder, onMarkComplete }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleAction = (action) => {
        action(assignment);
        handleMenuClose();
    };

    return (
        <ListItem
            key={assignment.id}
            disablePadding
            secondaryAction={
                <IconButton onClick={handleMenuClick}><MoreVertIcon /></IconButton>
            }
            sx={{ "&:hover": { bgcolor: alpha("#000", 0.04) }, borderRadius: 1.5, mb: 0.5 }}
        >
            <ListItemText
                primary={assignment.content_item?.title || 'Untitled Assignment'}
                secondary={
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                        <Chip {...getStatusChipProps(assignment.status, 'filled')} />
                        <Typography variant="caption" color="text.secondary">
                            Due: {assignment.due_date ? format(parseISO(assignment.due_date), 'P') : 'As Needed'}
                        </Typography>
                    </Stack>
                }
                onClick={() => onView(assignment)}
                sx={{ cursor: 'pointer', p: 1.5 }}
            />
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                <MenuItem onClick={() => handleAction(onView)}><ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>View Details</MenuItem>
                <MenuItem onClick={() => handleAction(onSendReminder)}><ListItemIcon><CampaignIcon fontSize="small" /></ListItemIcon>Send Reminder</MenuItem>
                <MenuItem onClick={() => handleAction(onMarkComplete)}><ListItemIcon><CheckCircleOutlineIcon fontSize="small" /></ListItemIcon>Mark Complete</MenuItem>
                <MenuItem onClick={() => handleAction(onDelete)} sx={{ color: 'error.main' }}><ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Cancel</MenuItem>
            </Menu>
        </ListItem>
    );
};

// --- MAIN COMPONENT ---
const EpisodeDetailModal = ({ open, onClose, patientId, tenantId, patientDetails, allEpisodes, isLoadingEpisodes, showSnackbar, workspaceData, isLoadingWorkspace, initialTab = 0, onOpenMedRec, onNewEpisode, allMedDirectives }) => { 
    const queryClient = useQueryClient();
    const [currentTab, setCurrentTab] = useState(initialTab);
    const [addDirectiveModal, setAddDirectiveModal] = useState({ open: false, targetEpisodeId: null });
    const [selectedContentToAdd, setSelectedContentToAdd] = useState([]);
    const [viewerModal, setViewerModal] = useState({ open: false, assignment: null });
    const [closeDialog, setCloseDialog] = useState({ open: false, episode: null });
    const [medsEndDate, setMedsEndDate] = useState(new Date());
    const [archiveConfirmation, setArchiveConfirmation] = useState({ open: false, episodeId: null, episodeTitle: '', userInput: '' });
    const [selectedEpisodeId, setSelectedEpisodeId] = useState(null);

    // [FIX] Add state for the Investigation Modal
    const [investigationModalState, setInvestigationModalState] = useState({
        open: false,
        taskType: null,
        taskId: null,
        title: '',
        data: null
    });

    useEffect(() => { // This useEffect should be kept as is
        if (open) { setCurrentTab(initialTab); }
    }, [open, initialTab]);

    const cleanupMutation = useMutation({
        mutationFn: (episodeId) => authFetch(`/episodes/${episodeId}/cancelled-assignments`, { method: 'DELETE' }),
        onSuccess: () => {
            showSnackbar("Cancelled assignments have been permanently removed.", "success");
            queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientId) });
        },
        onError: (err) => showSnackbar(`Cleanup failed: ${err.detail || 'Server error'}`, 'error'),
    });

    const deleteEpisodeMutation = useMutation({
        mutationFn: (episodeId) => authFetch(`/api/episodes/${episodeId}`, { method: 'DELETE' }),
        onSuccess: () => {
            showSnackbar("Episode has been permanently deleted.", "success");
            queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientId) });
            onClose(); // Close the modal after successful deletion
        },
        
       onError: (err) => {
            const errorDetail = err.data?.detail || err.message || "An unknown error occurred.";
            showSnackbar(`Delete failed: ${errorDetail}`, 'error');
        },
    
    });

    const archiveEpisodeMutation = useMutation({
        mutationFn: (episodeId) => authFetch(`/api/episodes/${episodeId}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'archived' }),
        }),
        onSuccess: () => {
            showSnackbar("Episode successfully archived.", "success");
            queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientId) });
            onClose(); // Close the modal
        },
        onError: (err) => {
            const errorDetail = err.data?.detail || err.message || "An unknown error occurred.";
            showSnackbar(`Archive failed: ${errorDetail}`, 'error');
        },
    });

    useEffect(() => { if (open) { setCurrentTab(initialTab); } }, [open, initialTab]);

    // --- MEMOIZED CALCULATIONS ---
    const { episodesForDisplay, perpetualEpisodeId } = useMemo(() => {
        if (!allEpisodes) return { episodesForDisplay: [], perpetualEpisodeId: null };
        const therapeuticEpisodes = allEpisodes.filter(ep => ep.episode_type !== 'lifetime_perpetual').sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const perpetualEp = allEpisodes.find(ep => ep.episode_type === 'lifetime_perpetual');
        return { episodesForDisplay: therapeuticEpisodes, perpetualEpisodeId: perpetualEp?.id };
    }, [allEpisodes]);
    
    useEffect(() => {
        // This effect now correctly re-evaluates whenever the list of available episodes changes.
        if (episodesForDisplay && episodesForDisplay.length > 0) {
            const latestActive = episodesForDisplay.find(ep => ep.status === 'active');
            if (latestActive) {
                // If an active episode exists, always default to it.
                setSelectedEpisodeId(latestActive.id);
            } else if (!selectedEpisodeId) {
                // Only default to the first (likely archived) episode if nothing is selected yet.
                setSelectedEpisodeId(episodesForDisplay[0].id);
            }
        }
    }, [episodesForDisplay]); // The dependency array is now corrected.

    const selectedEpisode = useMemo(() => {
        return episodesForDisplay.find(ep => ep.id === selectedEpisodeId);
    }, [selectedEpisodeId, episodesForDisplay]);
    
    const { 
        data: allPatientAssignments = [],
        isLoading: isLoadingAssignments 
    } = useQuery({
        queryKey: ['allPatientAssignments', patientId], 
        queryFn: () => authFetch(`/api/episodes/patient/${patientId}/assignments`),
        enabled: !!patientId,
        staleTime: 30 * 1000,
    });

    const assignmentsForSelectedEpisode = useMemo(() => {
        if (!selectedEpisodeId || !allPatientAssignments) {
            return [];
        }
        // Filter the master list to show only assignments for the selected episode
        return allPatientAssignments.filter(a => a.episode_of_care_id === selectedEpisodeId);
    }, [selectedEpisodeId, allPatientAssignments]);

    const assignmentCounts = useMemo(() => {
        if (isLoadingAssignments) return { total: 0 };
        // The count is now correctly based on the filtered list
        return { total: assignmentsForSelectedEpisode.length };
    }, [assignmentsForSelectedEpisode, isLoadingAssignments]);

    const sortedAssignments = useMemo(() => {
        if (!assignmentsForSelectedEpisode) return [];
        // The sorting logic now correctly operates on the filtered list
        const statusOrder = { 'SUBMITTED': 1, 'ASSIGNED': 2, 'COMPLETED': 3, 'CANCELLED': 4 };
        return [...assignmentsForSelectedEpisode].sort((a, b) => {
            const orderA = statusOrder[a.status?.toUpperCase()] || 99;
            const orderB = statusOrder[b.status?.toUpperCase()] || 99;
            if (orderA !== orderB) return orderA - orderB;
            const dateA = a.due_date ? new Date(a.due_date) : 0;
            const dateB = b.due_date ? new Date(b.due_date) : 0;
            return dateA - dateB;
        });
    }, [assignmentsForSelectedEpisode]); // Dependency is correctly on the filtered list

    const updateEpisodeStatusMutation = useMutation({
        mutationFn: ({ episodeId, status }) => authFetch(`/api/episodes/${episodeId}/status`, { method: 'PATCH', body: { status } }),
        onSuccess: () => {
            showSnackbar('Episode status updated.', 'success');
            queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientId) });
        },
        onError: (error) => {
            const errorDetail = error.data?.detail || error.message || 'Unknown error';
            showSnackbar(`Error updating status: ${errorDetail}`, 'error');
        },
    });

    const { data: allContentItems = [], isLoading: isLoadingContentItems } = useQuery({
        queryKey: contentItemsQueryKey(tenantId),
        queryFn: fetchAllContentItemsAPI,
        enabled: addDirectiveModal.open && !!tenantId,
        staleTime: 5 * 60 * 1000
    });

    const addDirectivesMutation = useMutation({
        mutationFn: (payload) => authFetch(`/api/directives/`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }),
        onSuccess: () => {
            showSnackbar("New assignments added.", "success");
            queryClient.invalidateQueries({ queryKey: ['episodeAssignments', selectedEpisodeId] });
            queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientId) });
            setAddDirectiveModal({ open: false, targetEpisodeId: null });
            setSelectedContentToAdd([]);
        },
        onError: (e) => showSnackbar(`Error: ${e.data?.detail || e.message}`, "error")
    });
    
    const handleContentConfigChange = useCallback((itemId, field, value) => { setSelectedContentToAdd(prev => prev.map(c => c.id === itemId ? { ...c, [field]: value } : c)); }, []);
    const handleAutocompleteChange = useCallback((event, newValues) => { const configuredValues = newValues.map(item => { const existingConfig = selectedContentToAdd.find(c => c.id === item.id); if (existingConfig) { return existingConfig; } const dueDate = new Date(); dueDate.setDate(dueDate.getDate() + 3); return { ...item, due_date: dueDate.toISOString().split('T')[0], reminder_frequency: ReminderFrequencyEnum.ONCE, }; }); setSelectedContentToAdd(configuredValues); }, [selectedContentToAdd]);
    const handleTabChange = (_, newValue) => setCurrentTab(newValue);
    
    const handleMutationSuccess = () => {
        showSnackbar("Action successful. Refreshing list...", "info");
        queryClient.invalidateQueries({ queryKey: ['allPatientAssignments', patientId] });
    };
    
    const cancelAssignmentMutation = useMutation({ mutationFn: (assignmentId) => authFetch(`/api/episodes/assignments/${assignmentId}`, { method: 'PATCH', body: { status: 'cancelled' } }), onSuccess: handleMutationSuccess, onError: (e) => showSnackbar(`Error cancelling assignment: ${e.data?.detail || e.message}`, "error"), });
    const updateAssignmentStatusMutation = useMutation({ mutationFn: ({ assignmentId, status }) => authFetch(`/api/episodes/assignments/${assignmentId}`, { method: 'PATCH', body: { status } }), onSuccess: handleMutationSuccess, onError: (e) => showSnackbar(`Error updating assignment: ${e.data?.detail || e.message}`, "error"), });
    const sendReminderMutation = useMutation({ mutationFn: (assignmentId) => authFetch(`/api/directives/assignments/${assignmentId}/send-reminder`, { method: 'POST' }), onSuccess: () => showSnackbar("On-demand reminder sent.", "success"), onError: (e) => showSnackbar(`Error: ${e.data?.detail || e.message}`, "error"), });
    const handleSendReminder = (assignment) => { sendReminderMutation.mutate(assignment.id); };
    const handleMarkComplete = (assignment) => { if (!assignment?.id) { showSnackbar("Cannot mark complete: Assignment ID is missing.", "error"); return; } updateAssignmentStatusMutation.mutate({ assignmentId: assignment.id, status: 'completed' }); };
    const handleDeleteAssignment = (assignment) => { if (assignment.id && window.confirm(`Are you sure you want to cancel the assignment "${assignment.content_item?.title || 'this item'}"?`)) { cancelAssignmentMutation.mutate(assignment.id); } };
    const generateEpisodeSummaryText = (episode) => { /* ... existing logic ... */ return "Summary text"; };
    const handleOpenCloseDialog = (episode) => { setCloseDialog({ open: true, episode: episode }); };
    const handleCloseEpisodeAction = (shouldGenerateReport) => { /* ... existing logic ... */ };
    const handleOpenAddModal = (episodeId) => { setSelectedContentToAdd([]); setAddDirectiveModal({ open: true, targetEpisodeId: episodeId }); };
    const handleMedWeekChange = useCallback((direction) => {
        setMedsEndDate(currentEndDate => {
            const newDate = new Date(currentEndDate);
            const dayAdjustment = direction === 'prev' ? -7 : 7;
            newDate.setDate(newDate.getDate() + dayAdjustment);
            
            // Prevent navigating into the future
            if (newDate > new Date()) {
                return new Date();
            }
            return newDate;
        });
    }, []);

    // [FIX] Updated handleViewDetails to actually set state and open the modal
    const handleViewDetails = useCallback((logItem) => {
        console.log("Viewing details for:", logItem);
        if (!logItem) return;
        setInvestigationModalState({
            open: true,
            taskType: logItem.type || 'log_entry', 
            taskId: logItem.id,
            title: logItem.item_name || 'Details',
            data: logItem
        });
    }, []);

    const handleCloseInvestigationModal = useCallback(() => {
        setInvestigationModalState({ open: false, taskType: null, taskId: null, title: '', data: null });
    }, []);

    const isToday = (someDate) => {
        const today = new Date();
        return someDate.getDate() === today.getDate() &&
            someDate.getMonth() === today.getMonth() &&
            someDate.getFullYear() === today.getFullYear();
    };
    const handleSaveNewDirectives = () => { if (!selectedContentToAdd.length || !patientId || !addDirectiveModal.targetEpisodeId) return; const regimenItems = selectedContentToAdd.map(item => ({ item_name: item.title, item_type: item.content_type, frequency_type: 'once', times_of_day: [], regimen_start_date: item.due_date || new Date().toISOString().split('T')[0], item_metadata: { content_item_id: item.id } })); addDirectivesMutation.mutate({ patient_user_id: patientId, episode_of_care_id: addDirectiveModal.targetEpisodeId, regimen_items: regimenItems }); };
    const handleOpenViewer = (assignment) => { if (assignment && assignment.content_item) { setViewerModal({ open: true, assignment: assignment }); } else { showSnackbar("Preview not available for this item.", "error"); } };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" scroll="paper">
            <DialogTitle sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">Clinician Workspace</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Reviewing Patient: {patientDetails?.first_name} {patientDetails?.last_name} (DOB: {patientDetails?.patient_profile?.date_of_birth ? format(parseISO(patientDetails.patient_profile.date_of_birth), 'P') : 'N/A'})
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button variant="outlined" size="small" onClick={onOpenMedRec}>Medications</Button>
                        <Button variant="contained" size="small" onClick={onNewEpisode}>New Episode</Button>
                        <IconButton onClick={onClose}><CloseIcon /></IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: 'grey.100' }}>
                 {!patientDetails ? (
                    <Box sx={{ p: 5, textAlign: 'center' }}> <Alert severity="warning">Patient data could not be loaded.</Alert> </Box>
                 ) : (
                    <>

                        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                            <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
                                <Tab label="Assignments" icon={<AssignmentIcon />} iconPosition="start" />
                                <Tab label="Medications" icon={<VaccinesIcon />} iconPosition="start" />
                                <Tab label="Daily Check-ins" icon={<FactCheckIcon />} iconPosition="start" />
                                <Tab label="Triage / Risk" icon={<DashboardIcon />} iconPosition="start" />
                            </Tabs>
                        </Box>
                        <Box sx={{ p: { xs: 2, md: 3 } }}>
                            <Box hidden={currentTab !== 0}>
                                {isLoadingEpisodes ? <CircularProgress /> :
                                    selectedEpisode ? (
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Stack spacing={2}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Select a Therapy Program</InputLabel>
                                                    
                                                    <Select
                                                        value={selectedEpisodeId || ''}
                                                        label="Select a Therapy Program"
                                                        onChange={(e) => setSelectedEpisodeId(e.target.value)}
                                                    >
                                                        {episodesForDisplay.map(ep => { // <-- Add curly brace
                                                            // +++ START OF FIX +++
                                                            // Prioritize the specific program name if available,
                                                            // fallback to the mapped title, then a generic default.
                                                            const programName = ep.program?.name || (EPISODE_TYPE_MAP[ep.episode_type?.toUpperCase()] || EPISODE_TYPE_MAP.DEFAULT).title || "Care Plan";
                                                            const episodeLabel = `${programName} (${formatDisplayValue(ep.status)})`;
                                                            // +++ END OF FIX +++

                                                            return ( // <-- Add return statement
                                                                <MenuItem key={ep.id} value={ep.id}>
                                                                    {episodeLabel} {/* <-- Use the generated label */}
                                                                </MenuItem>
                                                            ); // <-- Add closing parenthesis and semicolon
                                                        })} {/* <-- Add closing curly brace */}
                                                    </Select>
                                                </FormControl>
                                                <Divider />
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Assignments ({assignmentCounts.total} Actionable)
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Episode SaMD SN: {selectedEpisode.id}</Typography>
                                                    </Box>
                                                    
                                                    <Stack direction="row" spacing={1}>
                                                        {(selectedEpisode.total_assignments_count || 0) === 0 ? (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                onClick={() => {
                                                                    if (window.confirm("Are you sure you want to PERMANENTLY DELETE this empty episode?")) {
                                                                        deleteEpisodeMutation.mutate(selectedEpisode.id);
                                                                    }
                                                                }}
                                                                disabled={deleteEpisodeMutation.isPending}
                                                            >
                                                                Delete Episode
                                                            </Button>
                                                        ) : (
                                                            
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="warning"
                                                                onClick={() => setArchiveConfirmation({
                                                                    open: true,
                                                                    episodeId: selectedEpisode.id,
                                                                    episodeTitle: (EPISODE_TYPE_MAP[selectedEpisode.episode_type?.toUpperCase()] || EPISODE_TYPE_MAP.DEFAULT).title,
                                                                    userInput: ''
                                                                })}
                                                                disabled={archiveEpisodeMutation.isPending}
                                                            >
                                                                Archive Episode
                                                            </Button>

                                                        )}

                                                        <Button size="small" startIcon={<PlaylistAddIcon />} onClick={() => handleOpenAddModal(selectedEpisode.id)}>Add Assignment</Button>
                                                    </Stack>

                                                </Stack>
                                                {isLoadingAssignments ? <CircularProgress /> :
                                                    <List dense>
                                                        {sortedAssignments.length > 0 ? (
                                                            sortedAssignments.map(a => 
                                                                <AssignmentListItem key={a.id} assignment={a} onDelete={handleDeleteAssignment} onView={handleOpenViewer} onSendReminder={handleSendReminder} onMarkComplete={handleMarkComplete} />
                                                            )
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                                                No actionable assignments for this episode.
                                                            </Typography>
                                                        )}
                                                    </List>
                                                }
                                            </Stack>
                                        </Paper>
                                    ) : <Alert severity="info">No therapy programs found for this patient.</Alert>
                                }
                            </Box>
                            
                            <Box hidden={currentTab !== 1}>
                                <Suspense fallback={<CircularProgress />}>
                                    {isLoadingWorkspace ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                                    ) : workspaceData?.medications && workspaceData.medications.length > 0 ? (
                                        <MedicationsDashboard
                                            medications={workspaceData.medications}
                                            isLoading={isLoadingWorkspace}
                                            onDateChange={handleMedWeekChange}
                                            currentEndDate={medsEndDate}
                                            isNextDisabled={isToday(medsEndDate)}
                                        />
                                    ) : (
                                        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                                            <Typography color="text.secondary">No medication adherence data available for this patient.</Typography>
                                        </Paper>
                                    )}
                                </Suspense>
                            </Box>

                            <Box hidden={currentTab !== 2}> <Suspense fallback={<CircularProgress />}> {isLoadingWorkspace ? ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> ) : workspaceData ? ( <> <Box sx={{ mb: 3 }}> <ClinicianMiniCalendar calendarData={workspaceData.calendar_data} /> </Box> 
                            
                            <Grid container spacing={3}> 
                                <Grid item xs={12} lg={6}> <VitalsDashboard vitals={workspaceData.vitals} onOpenModal={handleViewDetails} /> </Grid> 
                                <Grid item xs={12} lg={6}> <MoodDashboard mood={workspaceData.mood} onOpenModal={handleViewDetails} /> </Grid> 
                                
                                {/* --- START OF FIX --- */}
                                
                                {/* NOTE: You are re-using 'SummariesDashboard' here, which is perfect 
                                because 'symptom_logs' is a List[AISummary], which matches the 'summaries' prop.
                                */}
                                <Grid item xs={12} lg={6}> 
                                    <SummariesDashboard 
                                        summaries={workspaceData.symptom_logs} 
                                        onOpenModal={handleViewDetails} 
                                        title="Symptoms & Pain" 
                                        icon={SymptomsIcon} 
                                    /> 
                                </Grid> 
                                
                                {/* NOTE: Use SummariesDashboard for activity. Your 'ActivityDashboard.js' component
                                expects complex {metrics, logs} data, but the API sends a simple list.
                                'SummariesDashboard' is built to handle the simple list correctly.
                                */}
                                <Grid item xs={12} lg={6}> 
                                    <SummariesDashboard 
                                        summaries={workspaceData.activity_logs} 
                                        onOpenModal={handleViewDetails} 
                                        title="Activity" 
                                        icon={ActivityIcon} 
                                    /> 
                                </Grid> 
                                
                                {/* This is the primary fix for your diet_fluid bug */}
                                <Grid item xs={12} lg={6}> 
                                    <SummariesDashboard 
                                        summaries={workspaceData.diet_gi_logs} 
                                        onOpenModal={handleViewDetails} 
                                        title="Diet & Nutrition" 
                                        icon={DietIcon} 
                                    /> 
                                </Grid> 
                                
                                {/* NOTE: Assuming 'JournalDashboard' is also built like 'SummariesDashboard' 
                                and expects a simple list prop.
                                */}
                                <Grid item xs={12} lg={6}> 
                                    <SummariesDashboard 
                                        summaries={workspaceData.journal_logs} 
                                        onOpenModal={handleViewDetails} 
                                        title="Journal" 
                                        icon={JournalIcon} 
                                    /> 
                                </Grid> 
                            
                            
                            </Grid> </> ) : ( <Alert severity="info">No Check-in data available to display.</Alert> )} </Suspense> </Box>
                            <Box hidden={currentTab !== 3}> <Suspense fallback={<CircularProgress />}> {isLoadingWorkspace ? <CircularProgress /> : workspaceData ? ( <Stack spacing={2}> <KeyIndicatorsDashboard checkIns={workspaceData.check_ins} onOpenModal={handleViewDetails} /> </Stack> ) : <Alert severity="info">No Triage & Risk data available.</Alert>} </Suspense> </Box>
                        </Box>
                    </>
                 )}
            </DialogContent>
            
            {viewerModal.open && (() => { const assignmentForViewer = viewerModal.assignment; let latestResponseData = {}; if (assignmentForViewer && Array.isArray(assignmentForViewer.responses) && assignmentForViewer.responses.length > 0) { const sortedResponses = [...assignmentForViewer.responses].sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)); if (sortedResponses[0] && sortedResponses[0].response_data) { latestResponseData = sortedResponses[0].response_data; } } return ( <Dialog open={viewerModal.open} onClose={() => setViewerModal({ open: false, assignment: null })} fullWidth maxWidth="md"> <DialogTitle>{assignmentForViewer?.content_item?.title || "Submission Viewer"}</DialogTitle> <DialogContent dividers> <ReadOnlySurveyRenderer patientInfo={patientDetails} blocks={assignmentForViewer?.content_item?.processed_content || []} patientResponses={latestResponseData} /> </DialogContent> <DialogActions> <Button onClick={() => setViewerModal({ open: false, assignment: null })}>Close</Button> </DialogActions> </Dialog> ); })()}
            <Dialog open={addDirectiveModal.open} onClose={() => setAddDirectiveModal({ open: false, targetEpisodeId: null })} fullWidth maxWidth="md"> <DialogTitle>Add New Assignments</DialogTitle> <DialogContent> <Autocomplete multiple fullWidth options={allContentItems} getOptionLabel={(o) => o.title || ''} value={selectedContentToAdd} onChange={handleAutocompleteChange} isOptionEqualToValue={(option, value) => option.id === value.id} loading={isLoadingContentItems} renderInput={(params) => <TextField {...params} label="Search Content Studio..." />} sx={{ mt: 1 }} /> <Divider sx={{ my: 2 }} /> {selectedContentToAdd.length > 0 && ( <List> {selectedContentToAdd.map((config) => ( <ListItem key={config.id} divider> <Grid container spacing={2} alignItems="center"> <Grid item xs={12} sm={5}> <ListItemText primary={config.title} secondary={config.content_type?.replace(/_/g, ' ')} /> </Grid> <Grid item xs={12} sm={4}> <TextField fullWidth size="small" label="Due Date" type="date" value={config.due_date} onChange={(e) => handleContentConfigChange(config.id, 'due_date', e.target.value)} InputLabelProps={{ shrink: true }} /> </Grid> <Grid item xs={12} sm={3}> <FormControl fullWidth size="small"> <InputLabel>Reminder</InputLabel> <Select label="Reminder" value={config.reminder_frequency} onChange={(e) => handleContentConfigChange(config.id, 'reminder_frequency', e.target.value)} > {Object.values(ReminderFrequencyEnum).map(freq => (<MenuItem key={freq} value={freq}>{freq}</MenuItem>))} </Select> </FormControl> </Grid> </Grid> </ListItem> ))} </List> )} </DialogContent> <DialogActions> <Button onClick={() => setAddDirectiveModal({ open: false, targetEpisodeId: null })}>Cancel</Button> <Button onClick={handleSaveNewDirectives} variant="contained" disabled={addDirectivesMutation.isPending || isLoadingContentItems}> Add Assignments </Button> </DialogActions> </Dialog>
            <Dialog open={closeDialog.open} onClose={() => setCloseDialog({ open: false, episode: null })} maxWidth="sm" fullWidth> <DialogTitle>Confirm Closing Episode</DialogTitle> <DialogContent> <Typography gutterBottom> You are about to close the episode: <strong>{closeDialog.episode?.episode_type}</strong>. </Typography> <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}> This will mark the episode as 'completed' and move it to historical records. This action cannot be undone. </Typography> <Alert severity="info" variant="outlined"> <strong>Next Step:</strong> Please choose an option below. You can generate a PDF summary for your records before closing, or close the episode directly. </Alert> </DialogContent> <DialogActions sx={{ p: 2 }}> <Button onClick={() => setCloseDialog({ open: false, episode: null })}>Cancel</Button> <Box sx={{flexGrow: 1}} /> <Button onClick={() => handleCloseEpisodeAction(false)} disabled={updateEpisodeStatusMutation.isPending} > Close Without Report </Button> <Button onClick={() => handleCloseEpisodeAction(true)} variant="contained" disabled={updateEpisodeStatusMutation.isPending} sx={{ ml: 1 }} > Generate Report & Close </Button> </DialogActions> </Dialog>
    
            <Dialog
                open={archiveConfirmation.open}
                onClose={() => setArchiveConfirmation({ ...archiveConfirmation, open: false })}
                maxWidth="sm"
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Archive Episode</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        You are about to archive the episode: <strong>{archiveConfirmation.episodeTitle}</strong>.
                        The episode will be moved to historical records and hidden from the active dashboard. This action cannot be easily undone.
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        To proceed, please type **ARCHIVE** in the box below.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Type 'ARCHIVE' to confirm"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={archiveConfirmation.userInput}
                        onChange={(e) => setArchiveConfirmation({ ...archiveConfirmation, userInput: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setArchiveConfirmation({ ...archiveConfirmation, open: false })}>Cancel</Button>
                    <Button
                        onClick={() => {
                            archiveEpisodeMutation.mutate(archiveConfirmation.episodeId);
                            setArchiveConfirmation({ open: false, episodeId: null, episodeTitle: '', userInput: '' });
                        }}
                        color="error"
                        variant="contained"
                        disabled={archiveConfirmation.userInput !== 'ARCHIVE' || archiveEpisodeMutation.isPending}
                    >
                        {archiveEpisodeMutation.isPending ? 'Archiving...' : 'Confirm Archive'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* [FIX] Render the InvestigationModal component */}
            {investigationModalState.open && (
                <InvestigationModal
                    open={investigationModalState.open}
                    onClose={handleCloseInvestigationModal}
                    patientId={patientId}
                    episodeId={selectedEpisodeId}
                    {...investigationModalState}
                />
            )}
        </Dialog>
    );
};

export default EpisodeDetailModal;